import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { californiaCities, lifeInsuranceKeywords } from '../data/californiaCities';
import { useTranslation } from '../hooks/useTranslation';

const { FiChevronDown, FiChevronUp, FiMapPin, FiShield } = FiIcons;

function CaliforniaCitiesFooter() {
  const [expandedSections, setExpandedSections] = useState({});
  const { t } = useTranslation();

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Group cities by region for better organization
  const cityRegions = {
    'Los Angeles Area': californiaCities.filter(city => 
      ['Los Angeles County', 'Orange County'].includes(city.county)
    ).slice(0, 12),
    'San Francisco Bay Area': californiaCities.filter(city => 
      ['San Francisco County', 'Santa Clara County', 'Alameda County', 'Contra Costa County'].includes(city.county)
    ).slice(0, 10),
    'San Diego Area': californiaCities.filter(city => 
      city.county === 'San Diego County'
    ).slice(0, 6),
    'Central Valley': californiaCities.filter(city => 
      ['Fresno County', 'Sacramento County', 'San Joaquin County', 'Stanislaus County', 'Tulare County'].includes(city.county)
    ).slice(0, 8),
    'Inland Empire': californiaCities.filter(city => 
      ['San Bernardino County', 'Riverside County'].includes(city.county)
    ).slice(0, 8),
    'Other Major Cities': californiaCities.filter(city => 
      !['Los Angeles County', 'Orange County', 'San Francisco County', 'Santa Clara County', 'Alameda County', 'Contra Costa County', 'San Diego County', 'Fresno County', 'Sacramento County', 'San Joaquin County', 'Stanislaus County', 'Tulare County', 'San Bernardino County', 'Riverside County'].includes(city.county)
    ).slice(0, 6)
  };

  const primaryKeywords = lifeInsuranceKeywords.slice(0, 6);
  const secondaryKeywords = lifeInsuranceKeywords.slice(6);

  return (
    <section className="bg-gray-800 text-white py-16 border-t border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('footer.coverageAcrossCA')}
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto">
            {t('footer.coverageSubtitle')}
          </p>
          <div className="flex items-center justify-center mt-4 text-blue-400">
            <SafeIcon icon={FiShield} className="mr-2" />
            <span className="text-sm">{t('header.licenseNumber')}</span>
          </div>
        </div>

        {/* Primary Keywords Section */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-white mb-6 text-center">
            {t('footer.popularProducts')}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {primaryKeywords.map((keyword, index) => (
              <div
                key={index}
                className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors"
              >
                <h4 className="font-semibold text-blue-400 mb-2">{keyword}</h4>
                <div className="text-sm text-gray-300">
                  {t('footer.availableThroughout')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cities by Region */}
        {Object.entries(cityRegions).map(([regionName, cities]) => (
          <div key={regionName} className="mb-8">
            <button
              onClick={() => toggleSection(regionName)}
              className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 rounded-lg p-4 transition-colors mb-4"
            >
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMapPin} className="text-blue-400" />
                <h3 className="text-xl font-semibold text-white">{regionName}</h3>
                <span className="text-gray-400 text-sm">({cities.length} {t('footer.cities')})</span>
              </div>
              <SafeIcon
                icon={expandedSections[regionName] ? FiChevronUp : FiChevronDown}
                className="text-gray-400"
              />
            </button>

            {expandedSections[regionName] && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
              >
                {cities.map((city, index) => (
                  <div key={index} className="bg-gray-700 rounded p-3">
                    <h4 className="font-medium text-white mb-2">{city.name}</h4>
                    <div className="text-xs text-gray-400 mb-2">{city.county}</div>
                    <div className="space-y-1">
                      {primaryKeywords.slice(0, 3).map((keyword, kidx) => (
                        <Link
                          key={kidx}
                          to={`/california/${city.name.toLowerCase().replace(/\s+/g, '-')}/${keyword.toLowerCase().replace(/\s+/g, '-')}`}
                          className="block text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          {keyword} in {city.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        ))}

        {/* Additional Keywords */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <button
            onClick={() => toggleSection('additional-keywords')}
            className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 rounded-lg p-4 transition-colors mb-4"
          >
            <h3 className="text-xl font-semibold text-white">{t('footer.additionalServices')}</h3>
            <SafeIcon
              icon={expandedSections['additional-keywords'] ? FiChevronUp : FiChevronDown}
              className="text-gray-400"
            />
          </button>

          {expandedSections['additional-keywords'] && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {secondaryKeywords.map((keyword, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-3">
                  <h4 className="font-medium text-blue-400 mb-2">{keyword}</h4>
                  <div className="text-xs text-gray-300">
                    {t('footer.availableAllCities')}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>

        {/* SEO Text Block */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              {t('footer.californiaLifeInsurance')}
            </h3>
            <div className="text-gray-300 text-sm space-y-3">
              <p>{t('footer.footerText1')}</p>
              <p>{t('footer.footerText2')}</p>
              <p>
                <strong>{t('footer.serviceAreas')}</strong> {t('footer.serviceAreasText')}
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.open('https://quote.burbankinsuranceservices.com/1b0DiB', '_blank')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
            {t('footer.getCaliforniaQuote')}
          </button>
          <p className="text-gray-400 text-sm mt-2">
            {t('footer.licensedInCA')}
          </p>
        </div>
      </div>
    </section>
  );
}

export default CaliforniaCitiesFooter;